import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (categoryId: string) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (cat) {
      setSelectedCategory(categoryId);
      setDropdownOpen(false);
      window.open(cat.url, '_blank', 'noopener,noreferrer');
    }
  };

  const selectedCat = categories.find((c) => c.id === selectedCategory);

  return (
    <section id="categorias" className="relative w-full bg-background-100 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Nova Coleção
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950">
            Categorias em <span className="text-accent-500">Destaque</span>
          </h2>
          <p className="text-foreground-600 mt-3 text-sm md:text-base max-w-xl mx-auto">
            Navegue pelas categorias mais procuradas e encontre os melhores produtos do CSSBuy Spreadsheet
          </p>
        </div>

        {/* Dropdown Selector */}
        <div className="max-w-md mx-auto mb-12 relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between bg-background-50 border border-background-300/60 rounded-xl px-5 py-3.5 text-foreground-700 cursor-pointer hover:border-accent-300 transition-colors duration-200 text-sm"
          >
            <span className={selectedCat ? 'text-foreground-950 font-medium' : 'text-foreground-500'}>
              {selectedCat ? selectedCat.name : 'Selecione uma categoria...'}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-xl shadow-sm z-50 max-h-80 overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleSelect(cat.id)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left cursor-pointer transition-colors duration-150 text-sm hover:bg-background-100 ${
                    selectedCategory === cat.id ? 'bg-accent-50 text-accent-700 font-medium' : 'text-foreground-700'
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Buttons Grid with Images */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-50 rounded-2xl border border-background-200/70 hover:border-accent-300 overflow-hidden transition-all duration-200 cursor-pointer hover:shadow-sm"
            >
              {/* Category Image */}
              <div className="relative w-full aspect-square bg-background-100 overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${cat.name} - CSSBuy Spreadsheet`}
                  title={`${cat.name} - CSSBuy Spreadsheet`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              </div>
              {/* Category Label */}
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-accent-100 transition-colors flex-shrink-0">
                  <i className={`${cat.icon} text-foreground-500 group-hover:text-accent-600 text-xs`}></i>
                </span>
                <span className="text-xs text-foreground-700 group-hover:text-foreground-950 font-medium leading-tight whitespace-nowrap truncate">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}